import { useState, MouseEvent, useCallback } from 'react';

import { parseFullName } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { Link, ROUTES } from 'libs/navigation';
import { useUser } from 'libs/providers';
import { Box, Pressed, Stack, Typography } from 'libs/ui';
import { Colors } from 'libs/ui/theme';

import { PopoverContentStyled, PopoverStyled, UserStyled } from './styles';

interface Props {
  hideChangePassword?: boolean;
}

const parseName = (firstName: string, lastName: string) =>
  (lastName.charAt(0) + firstName.charAt(0)).toUpperCase();

export const User = ({ hideChangePassword }: Props) => {
  const { user, logout } = useUser();

  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const closureEvent = useCallback(
    <T extends Function>(f: T) =>
      () => {
        handleClose();
        return f();
      },
    [handleClose]
  );

  if (!user) {
    return null;
  }

  return (
    <>
      <UserStyled datax="user" onClick={handleClick}>
        <Typography className="user-name" variant="bodyBold">
          {parseName(user.firstName, user.lastName)}
        </Typography>
      </UserStyled>

      <PopoverStyled
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={open}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}
      >
        <PopoverContentStyled className="headerUser-popoverContent">
          <Stack className="headerUser-popoverContent-wrapper">
            <Typography
              className="ellipsisText"
              title={parseFullName(user)}
              variant="bodySemiBold"
            >
              {parseFullName(user)}
            </Typography>
            <Typography color={Colors.gray600}>{user.email}</Typography>
          </Stack>
          <Box className="headerUser-popoverContent-line" />

          {user?.otp?.verified && !hideChangePassword && (
            <>
              <Link
                className="headerUser-popoverContent-wrapper headerUser-popoverContent-action"
                datax="settings"
                params={{}}
                to={ROUTES.settings.fullPath}
                onClick={handleClose}
              >
                <Typography>{t('common.settings')}</Typography>
              </Link>
              <Box className="headerUser-popoverContent-line" />
            </>
          )}

          <Stack className="headerUser-popoverContent-wrapper headerUser-popoverContent-action">
            <Pressed datax="logout" onClick={closureEvent(logout)}>
              <Typography color={Colors.red500}>
                {t('common.logout')}
              </Typography>
            </Pressed>
          </Stack>
        </PopoverContentStyled>
      </PopoverStyled>
    </>
  );
};
